import React, { useState, useCallback } from 'react';
import { Button, Divider, Input } from 'antd';
import {
  SendOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { getValidToken } from '../../services/apiClient';
import { backendTestEn } from '../../content/backendTest.en';
import styles from './BackendTest.module.css';

const API_URL = 'https://api.wanderertrip.com/jsontransfer';
const TEST_EMAIL = 'danieljpopp+OTA@gmail.com';
const CLIENTS_PULL_KEY = `wt_clients-${TEST_EMAIL}`;
const LIST_BUILDER_PULL_KEY = `wt_listbuilder-${TEST_EMAIL}`;

interface TestResult {
  ok: boolean;
  statusCode: number | null;
  body: string;
  durationMs: number;
  error?: string;
}

interface OperationState {
  loading: boolean;
  result: TestResult | null;
  requestInfo: { method: string; url: string; payload?: string } | null;
}

const EMPTY_OP: OperationState = { loading: false, result: null, requestInfo: null };

/** Test payload matching the jsontransfer envelope */
function buildTestPayload() {
  return {
    id: crypto.randomUUID(),
    type: 'connectionTest',
    body: {
      message: 'Backend connection test',
      timestamp: new Date().toISOString(),
    },
  };
}

/** Build auth headers using apiClient token management */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getValidToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/** Shared response parser */
async function parseResponse(response: Response): Promise<string> {
  try {
    const json = await response.json();
    return JSON.stringify(json, null, 2);
  } catch {
    const text = await response.text().catch(() => '');
    return text || '';
  }
}

/** Render a status badge */
const StatusBadge: React.FC<{ result: TestResult }> = ({ result }) => {
  if (result.error) {
    return (
      <span className={`${styles.statusBadge} ${styles.statusError}`}>
        <CloseCircleOutlined /> {backendTestEn.networkError}
      </span>
    );
  }
  if (result.ok) {
    return (
      <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>
        <CheckCircleOutlined /> {backendTestEn.success}
      </span>
    );
  }
  return (
    <span className={`${styles.statusBadge} ${styles.statusError}`}>
      <CloseCircleOutlined /> {backendTestEn.error}
    </span>
  );
};

/** Render request + response for an operation */
const OperationResult: React.FC<{ state: OperationState }> = ({ state }) => {
  const { result, requestInfo } = state;
  return (
    <>
      {requestInfo && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{backendTestEn.requestSection}</h3>
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{backendTestEn.method}:</span>
              <span className={styles.metaValue}>{requestInfo.method}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>{backendTestEn.url}:</span>
              <span className={styles.metaValue}>{requestInfo.url}</span>
            </div>
          </div>
          {requestInfo.payload && (
            <>
              <div className={styles.metaLabel} style={{ marginBottom: 4 }}>{backendTestEn.payload}</div>
              <pre className={styles.codeBlock}>{requestInfo.payload}</pre>
            </>
          )}
        </div>
      )}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{backendTestEn.responseSection}</h3>
        {!result ? (
          <div className={styles.noResponse}>{backendTestEn.noResponse}</div>
        ) : (
          <>
            <div className={styles.meta}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{backendTestEn.status}:</span>
                <StatusBadge result={result} />
              </div>
              {result.statusCode !== null && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{backendTestEn.statusCode}:</span>
                  <span className={styles.metaValue}>{result.statusCode}</span>
                </div>
              )}
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{backendTestEn.responseTime}:</span>
                <span className={styles.metaValue}>{result.durationMs} {backendTestEn.ms}</span>
              </div>
            </div>
            {result.error && <pre className={styles.codeBlock}>{result.error}</pre>}
            {result.body ? (
              <>
                <div className={styles.metaLabel} style={{ marginBottom: 4 }}>{backendTestEn.responseBody}</div>
                <pre className={styles.codeBlock}>{result.body}</pre>
              </>
            ) : result.ok && result.statusCode === 204 ? (
              <div className={styles.noResponse}>{backendTestEn.noContent}</div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

const BackendTest: React.FC = () => {
  const [lastId, setLastId] = useState<string>('');
  const [putState, setPutState] = useState<OperationState>(EMPTY_OP);
  const [getState, setGetState] = useState<OperationState>(EMPTY_OP);
  const [clientsPullState, setClientsPullState] = useState<OperationState>(EMPTY_OP);
  const [listBuilderPullState, setListBuilderPullState] = useState<OperationState>(EMPTY_OP);
  const [deleteState, setDeleteState] = useState<OperationState>(EMPTY_OP);

  /* ---------- PUT ---------- */
  const handlePut = useCallback(async () => {
    const testPayload = buildTestPayload();
    const authHeaders = await getAuthHeaders();
    setPutState({
      loading: true,
      result: null,
      requestInfo: { method: 'PUT', url: API_URL, payload: JSON.stringify(testPayload, null, 2) },
    });

    const start = performance.now();
    try {
      const response = await fetch(API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, application/problem+json',
          ...authHeaders,
        },
        body: JSON.stringify(testPayload),
      });
      const durationMs = Math.round(performance.now() - start);
      const body = await parseResponse(response);

      if (response.ok) {
        setLastId(testPayload.id);
      }
      setPutState((prev) => ({
        ...prev,
        loading: false,
        result: { ok: response.ok, statusCode: response.status, body, durationMs },
      }));
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      setPutState((prev) => ({
        ...prev,
        loading: false,
        result: { ok: false, statusCode: null, body: '', durationMs, error: err instanceof Error ? err.message : 'Unknown network error' },
      }));
    }
  }, []);

  /* ---------- GET ---------- */
  const handleGet = useCallback(async () => {
    if (!lastId) return;
    const authHeaders = await getAuthHeaders();
    const requestUrl = `${API_URL}?id=${encodeURIComponent(lastId)}`;
    setGetState({
      loading: true,
      result: null,
      requestInfo: { method: 'GET', url: requestUrl },
    });

    const start = performance.now();
    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json, application/problem+json',
          ...authHeaders,
        },
      });
      const durationMs = Math.round(performance.now() - start);
      const body = await parseResponse(response);

      setGetState((prev) => ({
        ...prev,
        loading: false,
        result: { ok: response.ok, statusCode: response.status, body, durationMs },
      }));
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      setGetState((prev) => ({
        ...prev,
        loading: false,
        result: { ok: false, statusCode: null, body: '', durationMs, error: err instanceof Error ? err.message : 'Unknown network error' },
      }));
    }
  }, [lastId]);

  const runGetForId = useCallback(
    async (id: string, setState: React.Dispatch<React.SetStateAction<OperationState>>) => {
      const authHeaders = await getAuthHeaders();
      const requestUrl = `${API_URL}?id=${encodeURIComponent(id)}`;
      setState({
        loading: true,
        result: null,
        requestInfo: { method: 'GET', url: requestUrl },
      });

      const start = performance.now();
      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json, application/problem+json',
            ...authHeaders,
          },
        });
        const durationMs = Math.round(performance.now() - start);
        const body = await parseResponse(response);

        setState((prev) => ({
          ...prev,
          loading: false,
          result: { ok: response.ok, statusCode: response.status, body, durationMs },
        }));
      } catch (err) {
        const durationMs = Math.round(performance.now() - start);
        setState((prev) => ({
          ...prev,
          loading: false,
          result: { ok: false, statusCode: null, body: '', durationMs, error: err instanceof Error ? err.message : 'Unknown network error' },
        }));
      }
    },
    []
  );

  const handleClientsPullGet = useCallback(async () => {
    await runGetForId(CLIENTS_PULL_KEY, setClientsPullState);
  }, [runGetForId]);

  const handleListBuilderPullGet = useCallback(async () => {
    await runGetForId(LIST_BUILDER_PULL_KEY, setListBuilderPullState);
  }, [runGetForId]);

  /* ---------- DELETE ---------- */
  const handleDelete = useCallback(async () => {
    if (!lastId) return;
    const authHeaders = await getAuthHeaders();
    const requestUrl = `${API_URL}?id=${encodeURIComponent(lastId)}`;
    setDeleteState({
      loading: true,
      result: null,
      requestInfo: { method: 'DELETE', url: requestUrl },
    });

    const start = performance.now();
    try {
      const response = await fetch(requestUrl, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/problem+json',
          ...authHeaders,
        },
      });
      const durationMs = Math.round(performance.now() - start);
      let body = '';
      if (response.status !== 204) {
        body = await parseResponse(response);
      }

      setDeleteState((prev) => ({
        ...prev,
        loading: false,
        result: { ok: response.ok, statusCode: response.status, body, durationMs },
      }));
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      setDeleteState((prev) => ({
        ...prev,
        loading: false,
        result: { ok: false, statusCode: null, body: '', durationMs, error: err instanceof Error ? err.message : 'Unknown network error' },
      }));
    }
  }, [lastId]);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <h1 className={styles.title}>{backendTestEn.pageTitle}</h1>
        <p className={styles.subtitle}>{backendTestEn.subtitle}</p>

        {lastId && (
          <div className={styles.idBanner}>
            <span className={styles.metaLabel}>{backendTestEn.lastId}:</span>
            <code className={styles.metaValue}>{lastId}</code>
          </div>
        )}

        {/* PUT */}
        <div className={styles.operationCard}>
          <h2 className={styles.operationTitle}>{backendTestEn.put.title}</h2>
          <p className={styles.operationDesc}>{backendTestEn.put.description}</p>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={putState.loading}
            onClick={handlePut}
            className={styles.opBtn}
          >
            {putState.loading ? backendTestEn.put.sending : backendTestEn.put.button}
          </Button>
          <OperationResult state={putState} />
        </div>

        <Divider />

        {/* GET */}
        <div className={styles.operationCard}>
          <h2 className={styles.operationTitle}>{backendTestEn.get.title}</h2>
          <p className={styles.operationDesc}>{backendTestEn.get.description}</p>
          <div className={styles.opRow}>
            <Input
              value={lastId}
              onChange={(e) => setLastId(e.target.value)}
              placeholder="Enter ID to retrieve"
              addonBefore={backendTestEn.get.idLabel}
              style={{ maxWidth: 500 }}
            />
          </div>
          <Button
            type="default"
            icon={<CloudDownloadOutlined />}
            loading={getState.loading}
            onClick={handleGet}
            disabled={!lastId}
            className={styles.opBtn}
          >
            {getState.loading ? backendTestEn.get.sending : backendTestEn.get.button}
          </Button>
          {!lastId && <div className={styles.hint}>{backendTestEn.get.noId}</div>}
          <OperationResult state={getState} />
        </div>

        <Divider />

        {/* GET pull keys */}
        <div className={styles.operationCard}>
          <h2 className={styles.operationTitle}>{backendTestEn.pullKeys.title}</h2>
          <p className={styles.operationDesc}>{backendTestEn.pullKeys.description}</p>
          <div className={styles.opRow}>
            <Input
              value={CLIENTS_PULL_KEY}
              readOnly
              addonBefore={backendTestEn.pullKeys.clientsIdLabel}
              style={{ maxWidth: 700 }}
            />
          </div>
          <Button
            type="default"
            icon={<CloudDownloadOutlined />}
            loading={clientsPullState.loading}
            onClick={handleClientsPullGet}
            className={styles.opBtn}
          >
            {clientsPullState.loading ? backendTestEn.pullKeys.running : backendTestEn.pullKeys.clientsButton}
          </Button>
          <OperationResult state={clientsPullState} />

          <Divider />

          <div className={styles.opRow}>
            <Input
              value={LIST_BUILDER_PULL_KEY}
              readOnly
              addonBefore={backendTestEn.pullKeys.listBuilderIdLabel}
              style={{ maxWidth: 700 }}
            />
          </div>
          <Button
            type="default"
            icon={<CloudDownloadOutlined />}
            loading={listBuilderPullState.loading}
            onClick={handleListBuilderPullGet}
            className={styles.opBtn}
          >
            {listBuilderPullState.loading ? backendTestEn.pullKeys.running : backendTestEn.pullKeys.listBuilderButton}
          </Button>
          <OperationResult state={listBuilderPullState} />
        </div>

        <Divider />

        {/* DELETE */}
        <div className={styles.operationCard}>
          <h2 className={styles.operationTitle}>{backendTestEn.delete.title}</h2>
          <p className={styles.operationDesc}>{backendTestEn.delete.description}</p>
          <div className={styles.opRow}>
            <Input
              value={lastId}
              onChange={(e) => setLastId(e.target.value)}
              placeholder="Enter ID to delete"
              addonBefore={backendTestEn.delete.idLabel}
              style={{ maxWidth: 500 }}
            />
          </div>
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={deleteState.loading}
            onClick={handleDelete}
            disabled={!lastId}
            className={styles.opBtn}
          >
            {deleteState.loading ? backendTestEn.delete.sending : backendTestEn.delete.button}
          </Button>
          {!lastId && <div className={styles.hint}>{backendTestEn.delete.noId}</div>}
          <OperationResult state={deleteState} />
        </div>
      </div>
    </div>
  );
};

export default BackendTest;
